​​import {
  Component,
  EventEmitter,
  Output,
  Input,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Renderer2,
  HostListener,
} from '@angular/core';
import { VendorService } from '@services/vendor.service';
import { Subscription } from 'rxjs';
import { AuthService } from '@core/auth.service';
import { MonthYearService } from '@services/monthyear.service';
import {
  ButtonLabels,
  LEGEND_ITEMS,
  LeaveType,
} from '@constants/constants';
import { ERROR_MESSAGES, SUMMARY_KEYS } from '@constants/vendor-constants';
import { SummaryItem } from '@interfaces/vendor.model';
import { HeaderText, LeaveTypeEnum, PersonaType } from '@constants/global.enum';
import { TimesheetCalendarHelper, MarkedDate } from './ops-timesheet-calendar.helper';
import { CommonModule } from '@angular/common';
import { EmeraldIconModule } from '@emerald-angular/icon';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { MatCalendar, DateRange } from '@angular/material/datepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { icon_fontSize } from '@pages/vendor-management/timesheet/timesheet-config';
import { FormsModule } from '@angular/forms';
import { dateFormat } from '@constants/dateFormat';
import { LeaveCommentsResponse, LeaveComment } from '@interfaces/vendor-management-model';
import { TimesheetCalendarService } from './ops-timesheet-calendar.service';
import { ToasterService } from '@services/toaster.service';
import { TIMESHEET_CONSTANTS } from '../ops-timesheet/ops-timesheet.constants';
import { TimesheetCalendarService as TimesheetCommentsService } from '@pages/vendor-rep/timesheet-calendar/timesheet-calendar.service';
import { BillingHighlightService } from '@shared/services/billing-highlight.service';

@Component({
  selector: 'app-ops-timesheet-calendar',
  standalone: true,
  imports: [
    CommonModule,
    EmeraldIconModule,
    EmeraldButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    FormsModule,
    MatCalendar,
  ],
  templateUrl: './ops-timesheet-calendar.component.html',
  styleUrls: ['./ops-timesheet-calendar.component.scss'],
})
export class TimesheetCalendarComponent {
  @ViewChild('calendar', { static: false }) calendar: any;
  @ViewChild('modeChoiceRef') modeChoiceRef!: ElementRef;

  @Input() candidateDetails: {
    employeeId: string;
    employeeName: string;
    jobTitle: string;
    location: string | null;
    vendorId: number | null;
    billingDates?: string[];
  } = {
    employeeId: '',
    employeeName: '',
    jobTitle: '',
    location: null,
    vendorId: null,
  };
  @Input() selectedyear: number = 0;
  @Input() selectedmonth: number = 0;
  @Input() componentName: string = '';

  @Output() closeComponent = new EventEmitter<void>();
  @Output() refreshTimesheet = new EventEmitter<void>();

  selectedDateValue!: Date;
  summary: SummaryItem[] = [];
  apiMarkedDates: MarkedDate[] = [];
  userMarkedDates: MarkedDate[] = [];
  commentHistory: LeaveComment[] = [];

  errorMessage: string = '';
  selectedLeaveDates: { [date: string]: string } = {};
  newMessage: string = '';
  currentCalendarMonth: number = 0;
  currentCalendarYear: number = 0;

  // ── Action dropdown ──────────────────────────────────────────────────────────
  selectedAction: string = '';
  leaveTypeOptions: { value: string; label: string }[] = [];

  // ── Selection state ──────────────────────────────────────────────────────────
  selectedRange: DateRange<Date> | null = null;
  firstClickDate: Date | null = null;
  pendingSecondDate: Date | null = null;
  individualPendingDates: Date[] = [];
  showModeChoicePopup: boolean = false;
  modeChoicePosition: { top: number; left: number } = { top: 0, left: 0 };
  hasUnselectPending: boolean = false;
  private _unselectHasLeave: boolean = false;
  private _unselectHasBilling: boolean = false;
  private _unselectBillingDates: string[] = [];
  private _unselectLeaveDates: string[] = [];
  private _skipNextDocumentClick: boolean = false;

  // ── Billing ──────────────────────────────────────────────────────────────────
  localBillingDates: string[] | null = null;
  selectedEmployeeTimesheet: any = { status: '' };

  headerTitle = HeaderText.LeaveSummary;
  legendItems = LEGEND_ITEMS;
  buttonLabels = ButtonLabels;
  iconFontSize = icon_fontSize;
  personaType = PersonaType;
  dateFormat = dateFormat;

  private _cachedBillingDateSet: Set<string> = new Set();
  private _cachedBillingKey: string = '';
  private subscriptions: Subscription = new Subscription();
  private timesheetHelper: TimesheetCalendarHelper;
  private lastFetchKey: string = '';
  private isLoadingData: boolean = false;
  private lastPolledMonth: number = -1;
  private lastPolledYear: number = -1;
  private calendarPollInterval: any;

  constructor(
    private vendorService: VendorService,
    private authService: AuthService,
    private monthYearService: MonthYearService,
    private renderer: Renderer2,
    private timesheetCalendarService: TimesheetCalendarService,
    private toasterService: ToasterService,
    private timesheetCommentsService: TimesheetCommentsService,
    private billingHighlightService: BillingHighlightService,
  ) {
    this.timesheetHelper = new TimesheetCalendarHelper();
    const saved = localStorage.getItem('apiMarkedDates');
    if (saved) this.apiMarkedDates = JSON.parse(saved);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedyear'] || changes['selectedmonth']) {
      this.fetchLeaveSummary();
    }
    if (changes['componentName']) {
      this.headerTitle =
        this.componentName === PersonaType.BillingHours
          ? HeaderText.BillingDate
          : HeaderText.LeaveSummary;
    }
    if (changes['candidateDetails']) {
      this._cachedBillingKey = '';
    }
  }

  ngOnInit(): void {
    if (!this.selectedyear || !this.selectedmonth) {
      const today = new Date();
      this.selectedyear = today.getFullYear();
      this.selectedmonth = today.getMonth() + 1;
    }
    this.selectedDateValue = new Date(this.selectedyear, this.selectedmonth - 1);
    this.headerTitle =
      this.componentName === PersonaType.BillingHours
        ? HeaderText.BillingDate
        : HeaderText.LeaveSummary;
    this.updateLeaveTypeOptions();

    this.subscriptions.add(
      this.monthYearService.month$.subscribe((month: number) => {
        if (month !== this.selectedmonth) {
          this.selectedmonth = month;
          this.fetchLeaveSummary();
        }
      }),
    );

    this.subscriptions.add(
      this.monthYearService.year$.subscribe((year: number) => {
        if (year !== this.selectedyear) {
          this.selectedyear = year;
          this.fetchLeaveSummary();
        }
      }),
    );
  }

  ngAfterViewInit() {
    this.calendarPollInterval = setInterval(() => {
      if (this.calendar?.activeDate) {
        const month = this.calendar.activeDate.getMonth() + 1;
        const year = this.calendar.activeDate.getFullYear();
        if (month !== this.lastPolledMonth || year !== this.lastPolledYear) {
          this.lastPolledMonth = month;
          this.lastPolledYear = year;
          this.selectedmonth = month;
          this.selectedyear = year;
          this._cachedBillingKey = '';
          this.fetchLeaveSummary();
        }
      }
    }, 300);
  }

  ngOnDestroy(): void {
    if (this.calendarPollInterval) clearInterval(this.calendarPollInterval);
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this._skipNextDocumentClick) {
      this._skipNextDocumentClick = false;
      return;
    }
    if (this.showModeChoicePopup) {
      const inside = this.modeChoiceRef?.nativeElement?.contains(event.target);
      if (!inside) {
        this.showModeChoicePopup = false;
        this.firstClickDate = null;
        this.pendingSecondDate = null;
        this.selectedRange = null;
        if (this.calendar) this.calendar.updateTodaysDate();
        this.refreshBillingHighlight();
      }
    }
  }

  // ── Fetch ────────────────────────────────────────────────────────────────────
  fetchLeaveSummary(): void {
    if (!this.isValidFetchRequest()) return;

    const key = `${this.candidateDetails.employeeId}-${this.candidateDetails.location}-${this.selectedmonth}-${this.selectedyear}`;
    if (this.lastFetchKey === key || this.isLoadingData) return;

    this.lastFetchKey = key;
    this.isLoadingData = true;
    this.localBillingDates = null;

    this.vendorService
      .getLeaveSummary(
        this.candidateDetails.employeeId,
        this.candidateDetails.location || '',
        this.candidateDetails.vendorId || null,
        this.selectedmonth,
        this.selectedyear,
      )
      .subscribe({
        next: (data: any) => {
          this.processLeaveSummary(data);
          this.loadLeaveComments();
          // Always fetch billing dates so both open paths have accurate data
          this.getTimesheetDetails(
            this.candidateDetails.employeeId,
            this.selectedmonth,
            this.selectedyear,
          );
          this.isLoadingData = false;
        },
        error: (error: any) => {
          this.handleFetchError(error);
          this.isLoadingData = false;
        },
      });
  }

  private isValidFetchRequest(): boolean {
    if (!this.candidateDetails.employeeId) {
      this.errorMessage = ERROR_MESSAGES.EMPLOYEE_ID_MISSING;
      return false;
    }
    if (!this.selectedmonth || !this.selectedyear) {
      this.errorMessage = ERROR_MESSAGES.MONTH_YEAR_MISSING;
      return false;
    }
    return true;
  }

  private processLeaveSummary(data: any): void {
    this.apiMarkedDates = this.timesheetHelper.processLeaveDates(data);
    localStorage.setItem('apiMarkedDates', JSON.stringify(this.apiMarkedDates));
    this.summary = this.timesheetHelper.processLeaveSummary(data, SUMMARY_KEYS);
    this.updateLeaveTypeOptions();
    this.timesheetHelper.refreshCalendar(this.apiMarkedDates, LeaveTypeEnum);
    this._cachedBillingKey = '';
    this.refreshBillingHighlight();
    if (this.calendar) this.calendar.updateTodaysDate();
  }

  // ── Billing date set ─────────────────────────────────────────────────────────
 private getBillingDateSet(): Set<string> {
  const billingDates = this.localBillingDates ?? this.candidateDetails?.billingDates ?? undefined;
  const key = `${this.selectedmonth}-${this.selectedyear}-${JSON.stringify(billingDates)}-${JSON.stringify(this._unselectBillingDates)}`;
  if (key !== this._cachedBillingKey) {
    this._cachedBillingKey = key;
    this._cachedBillingDateSet = this.billingHighlightService.getBillingDateSet(billingDates);
  }
  return this._cachedBillingDateSet;
}

  private getEffectiveBillingDateSet(): Set<string> {
    const full = this.getBillingDateSet();
    if (this._unselectBillingDates.length === 0) return full;
    const effective = new Set(full);
    this._unselectBillingDates.forEach((d) => effective.delete(d));
    return effective;
  }

  get hasBillingDatesForCurrentMonth(): boolean {
    return this.getBillingDateSet().size > 0;
  }

  private refreshBillingHighlight(): void {
    if (this.calendar) this.calendar.updateTodaysDate();
  }

  // ── Leave type options ───────────────────────────────────────────────────────
  private updateLeaveTypeOptions(): void {
    this.leaveTypeOptions = this.timesheetHelper.getLeaveTypes(this.summary, SUMMARY_KEYS);
  }

  // ── Summary / legend ─────────────────────────────────────────────────────────
  getFilteredSummary(): SummaryItem[] {
    return this.timesheetHelper.getFilteredSummary(this.summary, SUMMARY_KEYS);
  }

  getFilteredLegendItems() {
    const items = this.timesheetHelper.getFilteredLegendItems(
      this.legendItems,
      this.summary,
      SUMMARY_KEYS,
      LeaveType,
    );
    // Always include billing legend since billing is available in all open paths
    return [...items, { label: 'Billable Hours', class: 'last-working-day' }];
  }

  // ── dateClass ────────────────────────────────────────────────────────────────
  private getRestingClass(dateStr: string): string {
    if (this.getHalfLeaveCountForDate(dateStr) >= 2) return this.getDualHalfLeaveClass(dateStr);
    const user = this.userMarkedDates.find((md) => md.date === dateStr);
    if (user) {
      if (user.type === 'paid')       return 'paid';
      if (user.type === 'unpaid')     return 'unpaid';
      if (user.type === 'halfpaid')   return 'halfpaid';
      if (user.type === 'halfunpaid') return 'halfunpaid';
    }
    const api = this.apiMarkedDates.find((md) => md.date === dateStr);
    if (api) {
      if (api.type === 'paid')       return 'paid';
      if (api.type === 'unpaid')     return 'unpaid';
      if (api.type === 'halfpaid')   return 'halfpaid';
      if (api.type === 'halfunpaid') return 'halfunpaid';
      if (api.type === 'public')     return 'public-holiday';
    }
    if (this.getEffectiveBillingDateSet().has(dateStr)) return 'last-working-day';
    return '';
  }

  private resolveClass(dateStr: string): string {
    const isPublicHol = this.apiMarkedDates.some(
      (md) => md.date === dateStr && md.type === 'public',
    );
    if (this.selectedAction === 'billing') {
      if (isPublicHol) return 'public-holiday';
      const existingLeave =
        this.userMarkedDates.find((md) => md.date === dateStr) ||
        this.apiMarkedDates.find((md) => md.date === dateStr && md.type !== 'public');
      if (existingLeave) return this.getRestingClass(dateStr);
      return 'last-working-day';
    }
    if (isPublicHol) return 'public-holiday';
    switch (this.selectedAction) {
      case 'paid':       return 'paid';
      case 'unpaid':     return 'unpaid';
      case 'halfpaid':   return 'halfpaid';
      case 'halfunpaid': return 'halfunpaid';
      default:           return 'selected-range-start-end';
    }
  }

  private isInSelection(d: Date, dateStr: string): boolean {
    if (d.getDay() === 0 || d.getDay() === 6) return false;
    if (this.individualPendingDates.some((pd) => this.dateToString(pd) === dateStr)) return true;
    if (this.pendingSecondDate && this.dateToString(this.pendingSecondDate) === dateStr) return true;
    if (this.selectedRange?.start) {
      if (!this.selectedRange.end) return d.getTime() === this.selectedRange.start.getTime();
      return d >= this.selectedRange.start && d <= this.selectedRange.end;
    }
    return false;
  }

  get effectiveSelection(): DateRange<Date> | null {
    return this.selectedRange;
  }

  dateClass = (d: Date): string => {
    const dateStr = this.dateToString(d);

    // Dual half-leave takes precedence over everything — check before billing
    if (this.getHalfLeaveCountForDate(dateStr) >= 2) return this.getDualHalfLeaveClass(dateStr);

    // Billing highlight — always applied
    const dateSet = this.getEffectiveBillingDateSet();
    if (!this.isInSelection(d, dateStr)) {
      const billingClass = this.billingHighlightService.getBillingDateClass(
        d, dateSet, false, this.apiMarkedDates,
        (dt) => this.dateToString(dt),
      );
      if (billingClass) return billingClass;
    }

    if (this.isInSelection(d, dateStr)) return this.resolveClass(dateStr);

    return this.getRestingClass(dateStr);
  };

  // ── Action dropdown ──────────────────────────────────────────────────────────
  onActionChange(): void {
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.individualPendingDates = [];
    this._clearUnselectState();
    this.selectedRange = null;
    this.showModeChoicePopup = false;
    this.errorMessage = '';
    if (this.calendar) this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  hasSelectedDates(): boolean {
    return (
      this.individualPendingDates.length > 0 ||
      (!!this.selectedRange?.start && !!this.selectedRange?.end) ||
      this.hasUnselectPending
    );
  }

  applyAction(event?: Event): void {
    if (!this.selectedAction) {
      this.errorMessage = 'Please select an action from the dropdown first.';
      return;
    }
    if (!this.hasSelectedDates()) {
      this.errorMessage = 'Please select at least one date.';
      return;
    }
    if (this.selectedAction === 'billing') {
      this.considerBilling();
    } else if (this.selectedAction === 'unselect') {
      this._applyUnselect(event);
    } else {
      this._applyLeave(event);
    }
    this.selectedAction = '';
  }

  private _applyUnselect(event?: Event): void {
     if (!this.validateSubmission()) return;
    if (this._unselectHasBilling && this._unselectHasLeave) {
      const leaveDetails = this.prepareLeaveDetails();
      this.vendorService.createLeaveDetails(leaveDetails).subscribe({
        next: () => {
          const remaining = this._remainingBillingDates();
          this.vendorService
            .recalculateEmployeeTimesheet(
              this.candidateDetails.employeeId,
              remaining,
              remaining.length === 0 ? this.selectedmonth : undefined,
              remaining.length === 0 ? this.selectedyear : undefined,
            )
            .subscribe({
              next: (res) => {
                this.toasterService.showSuccess(res?.reason || 'Billing recalculated successfully', res?.status || 'Success');
                this._clearUnselectState();
                this.refreshTimesheet.emit();
                this.closeComponent.emit();
              },
              error: () => this.toasterService.showError('Failed to recalculate billing', 'Error'),
            });
        },
        error: (err) => this.handleSaveError(err),
      });
    } else if (this._unselectHasBilling) {
      const remaining = this._remainingBillingDates();
      this.vendorService
        .recalculateEmployeeTimesheet(
          this.candidateDetails.employeeId,
          remaining,
          remaining.length === 0 ? this.selectedmonth : undefined,
          remaining.length === 0 ? this.selectedyear : undefined,
        )
        .subscribe({
          next: (res) => {
            this.toasterService.showSuccess(res?.reason || 'Billing recalculated successfully', res?.status || 'Success');
            this._clearUnselectState();
            this.refreshTimesheet.emit();
            this.closeComponent.emit();
          },
          error: () => this.toasterService.showError('Failed to recalculate billing', 'Error'),
        });
    } else if (this._unselectHasLeave) {
      const existingBilling = this.localBillingDates ?? this.candidateDetails?.billingDates ?? [];
      if (existingBilling.length > 0) {
        const leaveDetails = this.prepareLeaveDetails();
        this.vendorService.createLeaveDetails(leaveDetails).subscribe({
          next: () => {
            const reAdd = this._unselectLeaveDates.map((d) => {
              const [y, m, day] = d.split('-');
              return `${day}-${m}-${y}`;
            });
            const merged = Array.from(new Set([...existingBilling, ...reAdd]));
            this.vendorService
              .recalculateEmployeeTimesheet(
                this.candidateDetails.employeeId,
                merged,
                merged.length === 0 ? this.selectedmonth : undefined,
                merged.length === 0 ? this.selectedyear : undefined,
              )
              .subscribe({
                next: (res) => {
                  this.toasterService.showSuccess(res?.reason || 'Billing recalculated successfully', res?.status || 'Success');
                  this._clearUnselectState();
                  this.refreshTimesheet.emit();
                  this.closeComponent.emit();
                },
                error: () => this.toasterService.showError('Failed to recalculate billing', 'Error'),
              });
          },
          error: (err) => this.handleSaveError(err),
        });
      } else {
        this.submit(event);
        this._clearUnselectState();
      }
    }
  }

  private _remainingBillingDates(): string[] {
    const fullSet = this.getBillingDateSet();
    const removedSet = new Set(this._unselectBillingDates);
    return Array.from(fullSet)
      .filter((d) => !removedSet.has(d))
      .map((d) => {
        const [y, m, day] = d.split('-').map(Number);
        return this.formatWorkingDate(new Date(y, m - 1, day));
      });
  }

  private _applyLeave(event?: Event): void {
    const isHolidayDate = (s: string) =>
      this.apiMarkedDates.some((md) => md.date === s && md.type === 'public');

    // Collect all valid leave dates BEFORE setDateType clears the selection state.
    const selectedLeaveDatesList: string[] = [];

    if (this.individualPendingDates.length > 0) {
      this.individualPendingDates.forEach((d) => {
        const day = d.getDay();
        if (day !== 0 && day !== 6) {
          const s = this.dateToString(d);
          if (!isHolidayDate(s)) selectedLeaveDatesList.push(s);
        }
      });
    } else if (this.selectedRange?.start && this.selectedRange?.end) {
      const cur = new Date(this.selectedRange.start);
      while (cur <= this.selectedRange.end!) {
        const day = cur.getDay();
        if (day !== 0 && day !== 6) {
          const s = this.dateToString(cur);
          if (!isHolidayDate(s)) selectedLeaveDatesList.push(s);
        }
        cur.setDate(cur.getDate() + 1);
      }
    }

    // Mark dates locally
    this.setDateType(this.selectedAction, event);

    // Validate that at least one valid working day was actually marked
    if (
      this.userMarkedDates.length === 0 &&
      Object.keys(this.selectedLeaveDates).length === 0
    ) {
      this.errorMessage =
        'Selected dates contain only public holidays or weekends. Please select valid working days.';
      this.selectedAction = '';
      return;
    }

    const existing = this.localBillingDates ?? this.candidateDetails?.billingDates ?? [];

    if (existing.length > 0) {
      // FIX: Billing array is non-empty — call leaves API then billing API,
      // merging ALL selected leave dates into the billing array regardless of
      // whether they were previously billed dates.
      if (!this.validateSubmission()) return;
      const leaveDetails = this.prepareLeaveDetails();
      this.vendorService.createLeaveDetails(leaveDetails).subscribe({
        next: () => {
          // Convert YYYY-MM-DD → DD-MM-YYYY for the billing API
          const leaveDatesFormatted = selectedLeaveDatesList.map((d) => {
            const [y, m, day] = d.split('-');
            return `${day}-${m}-${y}`;
          });
          const merged = Array.from(new Set([...existing, ...leaveDatesFormatted]));

          this.vendorService
            .recalculateEmployeeTimesheet(
              this.candidateDetails.employeeId,
              merged,
              merged.length === 0 ? this.selectedmonth : undefined,
              merged.length === 0 ? this.selectedyear : undefined,
            )
            .subscribe({
              next: (res) => {
                this.toasterService.showSuccess(
                  res?.reason || 'Billing recalculated successfully',
                  res?.status || 'Success',
                );
                this.selectedLeaveDates = {};
                this.userMarkedDates = [];
                this.refreshTimesheet.emit();
                this.closeComponent.emit();
              },
              error: () =>
                this.toasterService.showError('Failed to recalculate billing', 'Error'),
            });
        },
        error: (err) => this.handleSaveError(err),
      });
    } else {
      // No billing array — leaves only, normal submit
      this.submit(event);
    }
  }

  // ── Date click handler ───────────────────────────────────────────────────────
  onDateSelected(event: Date | null): void {
    this.errorMessage = '';

    if (
      this.selectedEmployeeTimesheet?.status === 'Approved' ||
      this.selectedEmployeeTimesheet?.status === 'Pending Approval'
    ) {
      this.errorMessage = `Cannot modify timesheet when status is ${this.selectedEmployeeTimesheet.status}`;
      return;
    }

    if (!this.selectedAction) {
      this.errorMessage = 'Please select an action from the dropdown first.';
      return;
    }

    if (!event) {
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      return;
    }

    const day = event.getDay();
    if (day === 0 || day === 6) {
      this.errorMessage = ERROR_MESSAGES.WEEKEND_LEAVE_NOT_ALLOWED;
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      return;
    }

    const dateStr = this.dateToString(event);
    const isPublicHoliday = this.apiMarkedDates.some(
      (md) => md.date === dateStr && md.type === 'public',
    );
    if (isPublicHoliday) {
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      return;
    }

    // Block changing an existing leave on a single date click
    const existingApiLeave = this.apiMarkedDates.find(
      (md) => md.date === dateStr && md.type !== 'public',
    );
    const isNewHalfAction = this.selectedAction === 'halfpaid' || this.selectedAction === 'halfunpaid';
    const existingHasHalf =
      existingApiLeave?.type === 'halfpaid' || existingApiLeave?.type === 'halfunpaid' ||
      this.userMarkedDates.some((md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid'));
    const isComplementaryHalfDay = existingHasHalf && isNewHalfAction;
    if (isComplementaryHalfDay && this.getHalfLeaveCountForDate(dateStr) >= 2) {
      this.errorMessage = 'Maximum of 2 half-leaves can be applied per day.';
      return;
    }
    if (
      existingApiLeave &&
      !isComplementaryHalfDay &&
      this.selectedAction !== 'unselect' &&
      this.selectedAction !== 'billing' &&
      !this.firstClickDate &&
      this.individualPendingDates.length === 0
    ) {
      this.errorMessage = 'Leave type cannot be changed.';
      return;
    }

    // Unselect mode
    if (this.selectedAction === 'unselect') {
      const isBillingDate = this.getBillingDateSet().has(dateStr);

      const isExistingApiLeave = this.apiMarkedDates.some(
        (md) => md.date === dateStr && md.type !== 'public',
      );
      if (isExistingApiLeave) {
        this.errorMessage = 'Existing leave dates cannot be modified or unselected.';
        return;
      }

      const isLeaveDate =
        this.apiMarkedDates.some((md) => md.date === dateStr) ||
        this.userMarkedDates.some((md) => md.date === dateStr);

      this.errorMessage = '';
      this.userMarkedDates = this.userMarkedDates.filter((md) => md.date !== dateStr);
      this.apiMarkedDates = this.apiMarkedDates.filter((md) => md.date !== dateStr);
      delete this.selectedLeaveDates[dateStr];

      if (isBillingDate) {
        this._unselectHasBilling = true;
        if (!this._unselectBillingDates.includes(dateStr)) this._unselectBillingDates.push(dateStr);
      }
      if (isLeaveDate) {
        this._unselectHasLeave = true;
        if (!this._unselectLeaveDates.includes(dateStr)) this._unselectLeaveDates.push(dateStr);
      }

      this.hasUnselectPending = true;
      this.selectedRange = null;
      this.calendar.updateTodaysDate();
      this.refreshBillingHighlight();
      this.refreshCalendar();
      return;
    }

    // Accumulate individual dates
    if (this.individualPendingDates.length > 0) {
      if (!this.individualPendingDates.some((pd) => this.dateToString(pd) === dateStr)) {
        this.individualPendingDates.push(event);
      }
      this.selectedRange = new DateRange<Date>(event, event);
      this.calendar.updateTodaysDate();
      this.refreshBillingHighlight();
      return;
    }

    if (!this.firstClickDate) {
      this.firstClickDate = event;
      this.selectedRange = new DateRange<Date>(event, event);
    } else {
      this.pendingSecondDate = event;
      this.showModeChoiceForDate(event);
    }
    this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  // ── Mode choice popup ────────────────────────────────────────────────────────
  showModeChoiceForDate(date: Date): void {
    const calendarWrapper = document.querySelector('.calendar-wrapper');
    const cells = document.querySelectorAll('.mat-calendar-body-cell');
    let positioned = false;
    const popupWidth = 160;
    const popupHeight = 80;

    cells.forEach((cell) => {
      const htmlCell = cell as HTMLElement;
      const ariaLabel = htmlCell.getAttribute('aria-label');
      if (!ariaLabel) return;
      if (new Date(ariaLabel).toDateString() !== date.toDateString()) return;

      const cellRect = htmlCell.getBoundingClientRect();
      const wrapperRect = calendarWrapper
        ? calendarWrapper.getBoundingClientRect()
        : { top: 0, left: 0, bottom: 9999, right: 9999, width: 400 };

      let top = cellRect.bottom - wrapperRect.top + 4;
      if (cellRect.bottom + popupHeight > wrapperRect.bottom) {
        top = cellRect.top - wrapperRect.top - popupHeight - 4;
      }
      top = Math.max(4, top);

      const calendarWidth = wrapperRect.width * 0.8;
      let left = cellRect.left - wrapperRect.left + cellRect.width / 2 - popupWidth / 2;
      left = Math.max(4, Math.min(left, calendarWidth - popupWidth - 4));

      this.modeChoicePosition = { top, left };
      positioned = true;
    });

    if (!positioned) this.modeChoicePosition = { top: 200, left: 40 };
    this._skipNextDocumentClick = true;
    this.showModeChoicePopup = true;
  }

  chooseRangeMode(): void {
    if (!this.firstClickDate || !this.pendingSecondDate) return;
    this.showModeChoicePopup = false;
    const d1 = this.firstClickDate;
    const d2 = this.pendingSecondDate;
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.selectedRange = d2 < d1 ? new DateRange<Date>(d2, d1) : new DateRange<Date>(d1, d2);
    this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  chooseIndividualMode(): void {
    if (!this.firstClickDate || !this.pendingSecondDate) return;
    this.showModeChoicePopup = false;
    this.individualPendingDates = [this.firstClickDate, this.pendingSecondDate];
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.selectedRange = new DateRange<Date>(
      this.individualPendingDates[this.individualPendingDates.length - 1],
      this.individualPendingDates[this.individualPendingDates.length - 1],
    );
    this.calendar.updateTodaysDate();
    this.refreshBillingHighlight();
  }

  // ── Billing submission ───────────────────────────────────────────────────────
  considerBilling(): void {
    const billingDates: string[] = [];
    const isPublicHoliday = (s: string) =>
      this.apiMarkedDates.some((md) => md.date === s && md.type === 'public');
    const isLeave = (s: string) =>
      this.apiMarkedDates.some((md) => md.date === s && md.type !== 'public') ||
      this.userMarkedDates.some((md) => md.date === s);

    if (this.individualPendingDates.length > 0) {
      this.individualPendingDates.forEach((d) => {
        if (d.getDay() !== 0 && d.getDay() !== 6) {
          const s = this.dateToString(d);
          if (!isPublicHoliday(s) && !isLeave(s)) {
            billingDates.push(this.formatWorkingDate(d));
          }
        }
      });
    } else if (this.selectedRange?.start && this.selectedRange?.end) {
      const cur = new Date(this.selectedRange.start);
      while (cur <= this.selectedRange.end) {
        if (cur.getDay() !== 0 && cur.getDay() !== 6) {
          const s = this.dateToString(cur);
          if (!isPublicHoliday(s) && !isLeave(s)) {
            billingDates.push(this.formatWorkingDate(cur));
          }
        }
        cur.setDate(cur.getDate() + 1);
      }
    }

    const monthLeaves = this.apiMarkedDates
      .filter(
        (md) =>
          md.type !== 'public' &&
          md.date.startsWith(`${this.selectedyear}-${String(this.selectedmonth).padStart(2, '0')}`),
      )
      .map((md) => {
        const [y, m, d] = md.date.split('-');
        return `${d}-${m}-${y}`;
      });

    if (billingDates.length === 0 && monthLeaves.length === 0) return;

    const existing = this.localBillingDates ?? this.candidateDetails?.billingDates ?? [];
    const merged = Array.from(new Set([...existing, ...billingDates, ...monthLeaves]));

    this.vendorService
      .recalculateEmployeeTimesheet(
        this.candidateDetails.employeeId,
        merged,
        merged.length === 0 ? this.selectedmonth : undefined,
        merged.length === 0 ? this.selectedyear : undefined,
      )
      .subscribe({
        next: (res) => {
          this.toasterService.showSuccess(
            res?.reason || 'Billing recalculated successfully',
            res?.status || 'Success',
          );
          this.selectedRange = null;
          this.individualPendingDates = [];
          this.firstClickDate = null;
          this.selectedLeaveDates = {};
          this.userMarkedDates = [];
          this.refreshTimesheet.emit();
          this.closeComponent.emit();
        },
        error: () => this.toasterService.showError('Failed to recalculate billing', 'Error'),
      });
  }

  // ── Leave submission ─────────────────────────────────────────────────────────
  submit(event?: Event): void {
    if (event) event.preventDefault();
    if (!this.validateSubmission()) return;

    const quarterlyLimit =
      this.summary.find((item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT)?.value || 0;
    const leaveDetails =
      quarterlyLimit === 0 ? this.prepareUnpaidLeaveDetails() : this.prepareLeaveDetails();

    this.vendorService.createLeaveDetails(leaveDetails).subscribe(
      () => {
        this.fetchLeaveSummary();
        this.selectedLeaveDates = {};
        this.userMarkedDates = [];
        this.refreshTimesheet.emit();
        this.closeComponent.emit();
      },
      (err) => this.handleSaveError(err),
    );
  }

  private validateSubmission(): boolean {
    const currentUser = this.authService.userProfile;
    if (!currentUser?.email) {
      this.errorMessage = ERROR_MESSAGES.USER_PROFILE_MISSING;
      return false;
    }
    const quarterlyLimit = this.getSummaryValue(SUMMARY_KEYS.QUARTERLY_LIMIT);
    const quarterlyAvailed = this.getSummaryValue(SUMMARY_KEYS.QUARTERLY_AVAILED);
    const newPaidLeaves = Object.values(this.selectedLeaveDates).reduce((total, lt) => {
      if (lt === LeaveType.Paid) return total + 1;
      if (lt === LeaveType.HalfPaid) return total + 0.5;
      return total;
    }, 0);
    if (quarterlyLimit > 0 && quarterlyAvailed + newPaidLeaves > quarterlyLimit) {
      this.errorMessage = ERROR_MESSAGES.QUARTERLY_LIMIT_EXCEEDED;
      return false;
    }
    return true;
  }

  private getSummaryValue(key: string): number {
    return this.summary.find((item) => item.text === key)?.value || 0;
  }

  private prepareLeaveDetails(): any {
    const currentUser = this.authService.userProfile;
    const prefix = `${this.selectedyear}-${String(this.selectedmonth).padStart(2, '0')}-`;

    const allLeaves = [
      ...this.userMarkedDates
        .filter((md) => md.date.startsWith(prefix))
        .map((md) => ({ date: md.date, leaveType: md.label })),
      ...this.apiMarkedDates
        .filter((md) => md.type !== 'public' && md.date.startsWith(prefix))
        .map((md) => ({ date: md.date, leaveType: md.label })),
    ];

    const uniqueMap = new Map<string, { date: string; leaveType: string }>();
    const halfLeaveOccurrences = new Map<string, number>();
    allLeaves.forEach((l) => {
      const isHalf = l.leaveType === 'Half Paid Leave' || l.leaveType === 'Half Unpaid Leave';
      const baseKey = `${l.date}_${l.leaveType}`;
      if (isHalf) {
        const count = (halfLeaveOccurrences.get(baseKey) || 0) + 1;
        halfLeaveOccurrences.set(baseKey, count);
        if (count <= 2) uniqueMap.set(`${baseKey}_${count}`, l);
      } else {
        if (!uniqueMap.has(baseKey)) uniqueMap.set(baseKey, l);
      }
    });

    return {
      employeeId: this.candidateDetails.employeeId,
      employeeName: this.candidateDetails.employeeName,
      jobTitle: this.candidateDetails.jobTitle,
      location: this.candidateDetails.location,
      createdBy: currentUser?.email || null,
      updatedBy: currentUser?.email || null,
      vendorId: this.candidateDetails.vendorId,
      leaves: Array.from(uniqueMap.values()),
      month: this.selectedmonth,
      year: this.selectedyear,
    };
  }

  private prepareUnpaidLeaveDetails(): any {
    const currentUser = this.authService.userProfile;
    const prefix = `${this.selectedyear}-${String(this.selectedmonth).padStart(2, '0')}-`;

    const allLeaves = [
      ...this.userMarkedDates
        .filter((md) => md.date.startsWith(prefix))
        .map((md) => ({ date: md.date, leaveType: md.label })),
      ...this.apiMarkedDates
        .filter((md) => md.type !== 'public' && md.date.startsWith(prefix))
        .map((md) => ({ date: md.date, leaveType: md.label })),
    ];

    const uniqueMap = new Map<string, { date: string; leaveType: string }>();
    const halfLeaveOccurrences = new Map<string, number>();
    allLeaves.forEach((l) => {
      const isHalf = l.leaveType === 'Half Paid Leave' || l.leaveType === 'Half Unpaid Leave';
      const baseKey = `${l.date}_${l.leaveType}`;
      if (isHalf) {
        const count = (halfLeaveOccurrences.get(baseKey) || 0) + 1;
        halfLeaveOccurrences.set(baseKey, count);
        if (count <= 2) uniqueMap.set(`${baseKey}_${count}`, l);
      } else {
        if (!uniqueMap.has(baseKey)) uniqueMap.set(baseKey, l);
      }
    });

    return {
      employeeId: this.candidateDetails.employeeId,
      employeeName: this.candidateDetails.employeeName,
      jobTitle: this.candidateDetails.jobTitle,
      location: this.candidateDetails.location,
      createdBy: currentUser?.email || null,
      updatedBy: currentUser?.email || null,
      vendorId: this.candidateDetails.vendorId,
      leaves: Array.from(uniqueMap.values()),
      month: this.selectedmonth,
      year: this.selectedyear,
    };
  }

  // ── setDateType ──────────────────────────────────────────────────────────────
  setDateType(type: string | null, event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.selectedRange?.start || !this.selectedRange?.end) {
      this.errorMessage = ERROR_MESSAGES.WEEKEND_LEAVE_PROHIBITED;
      return;
    }

    const start = this.selectedRange.start;
    const end = this.selectedRange.end;
    let cur = new Date(start);

    if (type === null || type === '') {
      const isSingle = start.getTime() === end.getTime();
      while (cur <= end) {
        const dateStr = this.dateToString(cur);
        if (cur.getDay() !== 0 && cur.getDay() !== 6) {
          this.userMarkedDates = this.userMarkedDates.filter((md) => md.date !== dateStr);
          delete this.selectedLeaveDates[dateStr];
          if (isSingle) this.apiMarkedDates = this.apiMarkedDates.filter((md) => md.date !== dateStr);
        }
        cur.setDate(cur.getDate() + 1);
      }
      this.individualPendingDates.forEach((pd) => {
        const s = this.dateToString(pd);
        this.userMarkedDates = this.userMarkedDates.filter((md) => md.date !== s);
        delete this.selectedLeaveDates[s];
      });
      this.selectedRange = null;
      this.individualPendingDates = [];
      this.firstClickDate = null;
      this.pendingSecondDate = null;
      this.calendar.updateTodaysDate();
      this.refreshCalendar();
      return;
    }

    const typeLabelMap: { [key: string]: string } = {
      paid: LeaveType.Paid,
      halfpaid: LeaveType.HalfPaid,
      unpaid: LeaveType.Unpaid,
      halfunpaid: LeaveType.HalfUnpaid,
    };
    const label = typeLabelMap[type] ?? LeaveType.HalfUnpaid;

    while (cur <= end) {
      const dateStr = this.dateToString(cur);
      if (cur.getDay() !== 0 && cur.getDay() !== 6) {
        const isHoliday = this.apiMarkedDates.some((md) => md.date === dateStr && md.type === 'public');
        if (!isHoliday) {
          const existingApiLeaveForDate = this.apiMarkedDates.find(
            (md) => md.date === dateStr && md.type !== 'public',
          );
          const isNewHalf = type === 'halfpaid' || type === 'halfunpaid';
          const existingHasHalfForDate =
            existingApiLeaveForDate?.type === 'halfpaid' || existingApiLeaveForDate?.type === 'halfunpaid' ||
            this.userMarkedDates.some((md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid'));
          const isComplementaryHalfDay = existingHasHalfForDate && isNewHalf;
          if (existingApiLeaveForDate && !isComplementaryHalfDay) {
            this.errorMessage = 'Leave type cannot be changed.';
          } else if (isComplementaryHalfDay && this.getHalfLeaveCountForDate(dateStr) >= 2) {
            this.errorMessage = 'Maximum of 2 half-leaves can be applied per day.';
          } else {
            if (!isComplementaryHalfDay) {
              this.userMarkedDates = this.userMarkedDates.filter((md) => md.date !== dateStr);
            }
            this.userMarkedDates.push({ date: dateStr, type, label });
            this.selectedLeaveDates[dateStr] = label;
          }
        }
      }
      cur.setDate(cur.getDate() + 1);
    }

    this.selectedRange = null;
    this.calendar.updateTodaysDate();

    this.individualPendingDates.forEach((pd) => {
      const s = this.dateToString(pd);
      if (pd.getDay() === 0 || pd.getDay() === 6) return;
      const isHoliday = this.apiMarkedDates.some((md) => md.date === s && md.type === 'public');
      if (isHoliday) return;
      const existingApiLeaveForDate = this.apiMarkedDates.find(
        (md) => md.date === s && md.type !== 'public',
      );
      const isNewHalf = type === 'halfpaid' || type === 'halfunpaid';
      const existingHasHalfForDate =
        existingApiLeaveForDate?.type === 'halfpaid' || existingApiLeaveForDate?.type === 'halfunpaid' ||
        this.userMarkedDates.some((md) => md.date === s && (md.type === 'halfpaid' || md.type === 'halfunpaid'));
      const isComplementaryHalfDay = existingHasHalfForDate && isNewHalf;
      if (existingApiLeaveForDate && !isComplementaryHalfDay) {
        this.errorMessage = 'Leave type cannot be changed.';
      } else if (isComplementaryHalfDay && this.getHalfLeaveCountForDate(s) >= 2) {
        this.errorMessage = 'Maximum of 2 half-leaves can be applied per day.';
      } else {
        if (!isComplementaryHalfDay) {
          this.userMarkedDates = this.userMarkedDates.filter((md) => md.date !== s);
        }
        this.userMarkedDates.push({ date: s, type, label });
        this.selectedLeaveDates[s] = label;
      }
    });

    this.individualPendingDates = [];
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.refreshCalendar();
  }

  refreshCalendar(): void {
    setTimeout(() => {
      document.querySelectorAll('.mat-calendar-body-cell').forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const ariaLabel = htmlCell.getAttribute('aria-label');
        if (!ariaLabel) return;
        const cellDate = new Date(ariaLabel);
        if (cellDate.getFullYear() !== this.selectedyear || cellDate.getMonth() + 1 !== this.selectedmonth) return;
        const dateStr = this.dateToString(cellDate);
        htmlCell.classList.remove(LeaveTypeEnum.Paid, LeaveTypeEnum.Unpaid, LeaveTypeEnum.PublicHoliday, LeaveTypeEnum.HalfPaid, LeaveTypeEnum.HalfUnpaid, 'dual-half-leave', 'dual-halfpaid', 'dual-halfunpaid');
        const api = this.apiMarkedDates.find((md) => md.date === dateStr);
        if (api) {
          if (api.type === LeaveTypeEnum.Paid) htmlCell.classList.add(LeaveTypeEnum.Paid);
          if (api.type === LeaveTypeEnum.HalfPaid) htmlCell.classList.add(LeaveTypeEnum.HalfPaid);
          if (api.type === LeaveTypeEnum.Unpaid) htmlCell.classList.add(LeaveTypeEnum.Unpaid);
          if (api.type === LeaveTypeEnum.HalfUnpaid) htmlCell.classList.add(LeaveTypeEnum.HalfUnpaid);
          if (api.type === LeaveTypeEnum.Public) htmlCell.classList.add(LeaveTypeEnum.PublicHoliday);
        }
        const user = this.userMarkedDates.find((md) => md.date === dateStr);
        if (user) {
          if (user.type === LeaveTypeEnum.Paid) htmlCell.classList.add(LeaveTypeEnum.Paid);
          if (user.type === LeaveTypeEnum.HalfPaid) htmlCell.classList.add(LeaveTypeEnum.HalfPaid);
          if (user.type === LeaveTypeEnum.Unpaid) htmlCell.classList.add(LeaveTypeEnum.Unpaid);
          if (user.type === LeaveTypeEnum.HalfUnpaid) htmlCell.classList.add(LeaveTypeEnum.HalfUnpaid);
        }
        if (this.getHalfLeaveCountForDate(dateStr) >= 2) {
          htmlCell.classList.remove(LeaveTypeEnum.HalfPaid, LeaveTypeEnum.HalfUnpaid);
          htmlCell.classList.add(this.getDualHalfLeaveClass(dateStr));
        }
      });
      if (this.calendar) this.calendar.updateTodaysDate();
    }, 100);
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  private _clearUnselectState(): void {
    this.hasUnselectPending = false;
    this._unselectHasLeave = false;
    this._unselectHasBilling = false;
    this._unselectBillingDates = [];
    this._unselectLeaveDates = [];
    this.selectedLeaveDates = {};
    this.userMarkedDates = [];
  }

  private getHalfLeaveCountForDate(dateStr: string): number {
    const apiCount = this.apiMarkedDates.filter(
      (md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid')
    ).length;
    const userCount = this.userMarkedDates.filter(
      (md) => md.date === dateStr && (md.type === 'halfpaid' || md.type === 'halfunpaid')
    ).length;
    return apiCount + userCount;
  }

  private getDualHalfLeaveClass(dateStr: string): string {
    const hasPaid =
      this.apiMarkedDates.some((md) => md.date === dateStr && md.type === 'halfpaid') ||
      this.userMarkedDates.some((md) => md.date === dateStr && md.type === 'halfpaid');
    const hasUnpaid =
      this.apiMarkedDates.some((md) => md.date === dateStr && md.type === 'halfunpaid') ||
      this.userMarkedDates.some((md) => md.date === dateStr && md.type === 'halfunpaid');
    if (hasPaid && hasUnpaid) return 'dual-half-leave';
    if (hasPaid) return 'dual-halfpaid';
    return 'dual-halfunpaid';
  }

  private formatWorkingDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${date.getFullYear()}`;
  }

  private handleSaveError(error: any): void {
    this.toasterService.showError(ERROR_MESSAGES.SAVE_LEAVE_DETAILS_FAILED, 'Error');
  }

  private handleFetchError(error: any): void {
    this.toasterService.showError(ERROR_MESSAGES.FETCH_LEAVE_SUMMARY_FAILED, 'Error');
    this.errorMessage = ERROR_MESSAGES.NO_DATA_AVAILABLE;
    this.summary = [
      { text: SUMMARY_KEYS.TOTAL_LEAVES, value: 0 },
      { text: SUMMARY_KEYS.PAID_LEAVES, value: 0 },
      { text: SUMMARY_KEYS.UNPAID_LEAVES, value: 0 },
      { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: 0 },
      { text: SUMMARY_KEYS.QUARTERLY_AVAILED, value: 0 },
    ];
  }

  getTimesheetDetails(employeeId: any, month: any, year: any): void {
    this.vendorService.getTimesheetDetails(month, year, this.candidateDetails.vendorId).subscribe({
      next: (res: any) => {
        const found = res.data?.find((ts: any) => ts.employeeId === employeeId);
        this.selectedEmployeeTimesheet = found ?? { status: '' };
        this.localBillingDates = found?.billingDates ?? null;
        this._cachedBillingKey = '';
        this.refreshBillingHighlight();
        this.calendar?.updateTodaysDate();
      },
      error: () => {},
    });
  }

  close(): void {
    localStorage.removeItem('apiMarkedDates');
    this.selectedLeaveDates = {};
    this.closeComponent.emit();
  }

  cancel(): void {
    localStorage.removeItem('apiMarkedDates');
    this.selectedLeaveDates = {};
    this.selectedAction = '';
    this.firstClickDate = null;
    this.pendingSecondDate = null;
    this.individualPendingDates = [];
    this._clearUnselectState();
    this.selectedRange = null;
    this.showModeChoicePopup = false;
    this.errorMessage = '';
    if (this.calendar) this.calendar.updateTodaysDate();
    this.closeComponent.emit();
  }

  formatDate(dateString: string | number | Date): string {
    return this.timesheetHelper.formatDate(dateString);
  }

  dateToString(date: Date): string {
    return this.timesheetHelper.dateToString(date);
  }

  get todayFormatted() {
    return [{ date: this.timesheetHelper.dateToString(new Date()), text: 'Today' }];
  }

  displayMonth(): void {
    this.timesheetHelper.displayMonth(this.apiMarkedDates, this.todayFormatted, () => {});
  }

  streamOpened(): void {
    this.timesheetHelper.streamOpened(this.renderer, this.displayMonth.bind(this));
  }

  onViewChanged(): void {
    setTimeout(() => {
      const activeDate: Date = this.calendar?.activeDate;
      if (activeDate) {
        this.selectedmonth = activeDate.getMonth() + 1;
        this.selectedyear = activeDate.getFullYear();
        this.currentCalendarMonth = activeDate.getMonth() + 1;
        this.currentCalendarYear = activeDate.getFullYear();
        this.fetchLeaveSummary();
      }
    }, 0);
  }

  getCurrentCalendarDateForComments(): string {
    const month = this.currentCalendarMonth || this.selectedmonth || new Date().getMonth() + 1;
    const year = this.currentCalendarYear || this.selectedyear || new Date().getFullYear();
    const utcDate = new Date(Date.UTC(year, month - 1, 1));
    utcDate.setMinutes(utcDate.getMinutes() + 330);
    return utcDate.toISOString().split('T')[0];
  }

  private loadLeaveComments() {
    this.timesheetCommentsService
      .getLeaveComments(this.candidateDetails.employeeId, this.selectedmonth, this.selectedyear)
      .subscribe({
        next: (response: LeaveCommentsResponse) => {
          this.commentHistory = response?.data || [];
        },
        error: () => {
          this.toasterService.showError(
            TIMESHEET_CONSTANTS.MESSAGES.FAILED_LOAD_COMMENTS,
            TIMESHEET_CONSTANTS.TITLES.LOAD_ERROR,
          );
        },
      });
  }

  sendMessage() {
    const message = this.newMessage.trim();
    if (!message) return;

    const currentUser = this.authService.userProfile;
    const currentDate = new Date().toISOString();
    const commentData = {
      employeeId: this.candidateDetails.employeeId,
      commentedManagerName: currentUser?.firstName ?? '',
      managerComments: message,
      managerCommentsDate: currentDate,
      commentedVendorName: '',
      vendorComments: '',
      vendorCommentsDate: '',
      commentsDate: this.getCurrentCalendarDateForComments(),
    };

    this.timesheetCommentsService.saveComment(commentData).subscribe({
      next: () => {
        this.commentHistory?.push(commentData);
        this.newMessage = '';
        this.close();
        this.toasterService.showSuccess(
          TIMESHEET_CONSTANTS.MESSAGES.COMMENT_SAVED_SUCCESS,
          TIMESHEET_CONSTANTS.TITLES.SUCCESS,
        );
      },
      error: (error) => {
        this.toasterService.showError(
          error.error?.message || error.message || TIMESHEET_CONSTANTS.MESSAGES.FAILED_SAVE_COMMENT,
          TIMESHEET_CONSTANTS.TITLES.SAVE_ERROR,
        );
      },
    });
  }
}